import moment from 'moment';
import type { CalendarLeanEvent } from '../types';

export interface PositionedEvent {
    event: CalendarLeanEvent;
    dayIndex: number;
    topPx: number;
    heightPx: number;
    lane: number;
    laneCount: number;
}

export interface TimeLayoutOptions {
    /** One entry per rendered column; only the date part is used. */
    days: Date[];
    dayStartHour: number;
    dayEndHour: number;
    pxPerHour: number;
    /** Our events are instants, so the block height is fixed, not derived. */
    blockHeightPx: number;
}

/**
 * Place events on the week/day time axis.
 *
 * A scheduled send is a moment, not a range (the old calendar passed the same
 * accessor for start and end), so every block is the same height and events
 * that share a minute FAN OUT into lanes rather than covering each other —
 * which matters here, where a drip fires several sends at the same time.
 */
export function buildTimeLayout(
    events: CalendarLeanEvent[],
    { days, dayStartHour, dayEndHour, pxPerHour, blockHeightPx }: TimeLayoutOptions,
): PositionedEvent[] {
    const dayIndexByKey = new Map<string, number>();
    days.forEach((day, index) => dayIndexByKey.set(moment(day).format('YYYY-MM-DD'), index));

    const placed: PositionedEvent[] = [];

    for (const event of events) {
        const at = moment(event.dueDate);
        const dayIndex = dayIndexByKey.get(at.format('YYYY-MM-DD'));
        if (dayIndex === undefined) continue;

        const hours = at.hour() + at.minute() / 60 + at.second() / 3600;
        if (hours < dayStartHour || hours >= dayEndHour) continue;

        placed.push({
            event,
            dayIndex,
            topPx: (hours - dayStartHour) * pxPerHour,
            heightPx: blockHeightPx,
            lane: 0,
            laneCount: 1,
        });
    }

    // Lane assignment per column: walk the day's events in time order and open a
    // new cluster as soon as one starts below the previous cluster's bottom edge.
    for (let index = 0; index < days.length; index++) {
        const column = placed.filter(p => p.dayIndex === index).sort((a, b) => a.topPx - b.topPx);
        let cluster: PositionedEvent[] = [];
        let clusterBottom = -Infinity;

        const closeCluster = () => {
            cluster.forEach((p, lane) => { p.lane = lane; p.laneCount = cluster.length; });
            cluster = [];
        };

        for (const positioned of column) {
            if (positioned.topPx >= clusterBottom) {
                closeCluster();
                clusterBottom = positioned.topPx + positioned.heightPx;
            } else {
                clusterBottom = Math.max(clusterBottom, positioned.topPx + positioned.heightPx);
            }
            cluster.push(positioned);
        }
        closeCluster();
    }

    return placed;
}

/** The horizontal air a positioned chip gives up inside its lane — the `- 8px`
 *  in the chip's inline style, kept next to the layout that causes it. */
export const LANE_GUTTER_PX = 8;

/**
 * How wide a chip in this lane actually renders.
 *
 * The columns are `1fr` tracks, so the only thing this layout knows up front is
 * the chip's SHARE of its day column (1 / laneCount); the column's own pixel
 * width has to come from the caller. It lives here, beside the lane assignment
 * that defines the share, so the two can never drift apart — and because that
 * rendered width is what decides which parts of a chip are worth drawing at all
 * (see planChipContent).
 */
export function laneWidthPx(columnWidthPx: number, laneCount: number): number {
    if (!Number.isFinite(columnWidthPx) || !Number.isFinite(laneCount) || laneCount < 1) return 0;
    return Math.max(0, columnWidthPx / laneCount - LANE_GUTTER_PX);
}
